import { DestroyRef } from '@angular/core';

const RETRY_INTERVAL_MS = 250;
const RETRY_ATTEMPTS = 20;
const THEME_ATTRIBUTES = ['theme', 'mode'];

/** Resolves the variable the way a stylesheet does — as the computed
    `background-image` of a probe element — because Safari's `getPropertyValue`
    on the root does not reliably return a theme's `url()` token. */
export function readCssUrlVariable(name: string): string | null {
  const probe = document.createElement('span');
  probe.style.cssText = `position:absolute;width:0;height:0;visibility:hidden;background-image:var(${name})`;
  document.body.appendChild(probe);
  let resolved = getComputedStyle(probe).backgroundImage;
  probe.remove();
  if (!resolved || resolved.includes('var('))
    resolved = getComputedStyle(document.documentElement).getPropertyValue(name);
  return resolved.match(/url\(\s*['"]?([^'")]+?)['"]?\s*\)/)?.[1] ?? null;
}

/** Calls `apply` with the url of a theme variable such as `--publicShellBranding`,
    and again with the new url (or `null`) whenever the theme is switched.
    The theme lives in the non-blocking `styles.css` (`media="print"` until it has
    loaded), and WebKit fires the window `load` before it applies — so a first miss
    is retried on every stylesheet `load` and on a short, bounded interval. */
export function watchCssUrlVariable(
  name: string,
  apply: (url: string | null) => void,
  destroyRef: DestroyRef
): void {
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  );
  let current: string | null = null;
  let attempts = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stopRetries = () => {
    window.removeEventListener('load', retry);
    for (const link of links) link.removeEventListener('load', retry);
    if (timer) clearInterval(timer);
    timer = null;
  };

  const update = () => {
    const url = readCssUrlVariable(name);
    if (url === current) return;
    current = url;
    apply(url);
  };

  function retry(): void {
    update();
    if (current) stopRetries();
  }

  const themeObserver = new MutationObserver(update);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: THEME_ATTRIBUTES,
  });
  destroyRef.onDestroy(() => {
    stopRetries();
    themeObserver.disconnect();
  });

  update();
  if (current) return;
  window.addEventListener('load', retry);
  for (const link of links) link.addEventListener('load', retry);
  timer = setInterval(() => {
    retry();
    if (++attempts >= RETRY_ATTEMPTS) stopRetries();
  }, RETRY_INTERVAL_MS);
}
